import React, { useState, useEffect } from 'react';
import HyperLink from 'components/Core/HyperLink';
import { globalHistory } from '@reach/router';
import Popover from 'components/Core/Popover';
import MenuIcon from '@mui/icons-material/Menu';
import BookmarksIcon from 'components/Brand/Icons/Toolbox/BookmarksIcon';
import BurgerIcon from 'components/Brand/Icons/Toolbox/NavBar/BurgerIcon';
import { isInPath } from 'utils/urlHelper';
import { navigationEvent } from 'services/dataLayerService';
import useBreakpointView from 'utils/useBreakpointView';
import useIsScrolling from 'hooks/useIsScrolling';
import useExternalBanner from 'hooks/useExternalBanner';
import UserAvatar from '../UserAvatar';
import {
  SubNavContainer,
  IconContainer,
  NavRow,
  NavCol,
  NavSeparator,
  UserIconContainer,
  NavTitleCol,
  NavRowUser,
  MobileSubNavContainer,
  IconCluster,
  BurgerContainer,
} from './styles';
import MobileCloseIcon from './MobileCloseIcon';
import AccountMenu from './AccountMenu';

const SubNav = ({ data = [], openLoginModal, userInfo, onLogOut, location }) => {
  const [isMounted, setIsMounted] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [burgerClick, setBurgerClick] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isMobile = useBreakpointView(['xs', 'sm', 'md', 'lg']);
  const [scrollPosition, setScrollPosition] = useState(0);
  const bannerHeight = useExternalBanner();

  useIsScrolling(
    () => setIsCollapsed(true),
    () => setIsCollapsed(false),
  );

  const handleMouseOverEvents = (bool) => !burgerClick && !isMobileMenuOpen && setIsOpen(bool);
  const handleMobileMenuToggle = (bool) => {
    if (!bool) {
      const container = document.querySelector('#___gatsby');
      container?.removeAttribute('style');
      document.body.style.height = null;
      window.scrollTo(0, scrollPosition);
    }

    setIsOpen(bool);
    setMobileMenuOpen(bool);
  };

  const handleOMPromo = () => {
    const omPromo = document.getElementsByClassName('sliding-om-promo')?.[0];
    if (omPromo) {
      const omPromoStyles = omPromo.getAttribute('style');

      const handleMarginStyle = () => {
        if (window?.innerWidth <= 1199) {
          return isCollapsed ? '90px' : '250px';
        }
        return isOpen ? '250px' : '90px';
      };

      omPromo.setAttribute(
        'style',
        `${omPromoStyles}margin-left: ${handleMarginStyle()} !important;`,
      );
    }
  };

  useEffect(() => {
    setIsMounted(true);
    window.addEventListener('resize', handleOMPromo);
    return () => {
      window.removeEventListener('resize', handleOMPromo);
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    handleOMPromo();
  }, [isMobile, isOpen, isCollapsed]);

  useEffect(() => {
    const container = document.querySelector('#___gatsby');
    if (isMounted && isMobile && isOpen) {
      if (container) {
        const scrollPos = window.pageYOffset;
        container.style.top = `-${scrollPos}px`;
        container.style.position = 'fixed';
        container.style.width = '100%';
        document.body.style.height = 'calc(100vh - env(safe-area-inset-bottom))';
        setScrollPosition(scrollPos);
      }
    }

    return () => {
      container.removeAttribute('style');
    };
  }, [isMobile, isOpen]);

  const accountMenu = userInfo && (
    <AccountMenu
      userInfo={userInfo}
      onLogOut={() => {
        setIsPopoverOpen(false);
        onLogOut();
      }}
    />
  );

  return (
    <>
      <MobileSubNavContainer
        id="mobile-toolbox-subnav"
        onClick={() => handleMobileMenuToggle(true)}
        className={`toolbox-mobile-subnav ${isOpen ? 'open' : ''}`}
      >
        <MenuIcon />
      </MobileSubNavContainer>
      <SubNavContainer
        id="toolbox-subnav"
        onMouseLeave={() => handleMouseOverEvents(false)}
        onMouseEnter={() => handleMouseOverEvents(true)}
        className={isOpen ? 'open' : ''}
        bannerHeight={bannerHeight}
      >
        <BurgerContainer
          className={burgerClick ? 'open' : ''}
          onClick={() => setBurgerClick(!burgerClick)}
        >
          <BurgerIcon />
        </BurgerContainer>
        <MobileCloseIcon onClick={() => handleMobileMenuToggle(false)} />
        <IconCluster>
          <NavRow
            className={
              isInPath(location.href, '/toolbox', [
                '/toolbox/bookmarks',
                '/toolbox/settings',
                '/toolbox/security',
              ])
                ? 'selected'
                : ''
            }
          >
            <HyperLink href="/toolbox" ctaPosition="toolbox-subnav">
              <NavCol>
                <IconContainer>
                  <data.logo.Icon />
                </IconContainer>
              </NavCol>
              <NavTitleCol>
                <h6>Home</h6>
              </NavTitleCol>
            </HyperLink>
          </NavRow>
          <NavSeparator />
          {data?.navigation?.map(({ slug, icon: Icon, title }, index) => {
            return (
              <NavRow
                className={
                  isInPath(
                    location.href,
                    slug,
                    data.navigation.map((x) => x.slug),
                  )
                    ? 'selected'
                    : ''
                }
                key={`nav-row-${index}`}
              >
                <HyperLink href={slug} ctaPosition="toolbox-subnav">
                  <NavCol>
                    {Icon && (
                      <IconContainer>
                        <Icon />
                      </IconContainer>
                    )}
                  </NavCol>
                  <NavTitleCol>
                    <h6>{title}</h6>
                  </NavTitleCol>
                </HyperLink>
              </NavRow>
            );
          })}
        </IconCluster>
        {!userInfo && (
          <NavRowUser className="nav-signin-row">
            <HyperLink
              onClick={() => {
                openLoginModal();
                navigationEvent('Sign In', null, 'toolbox-subnav');
              }}
            >
              <NavCol>
                <UserIconContainer>
                  <data.user.Icon />
                </UserIconContainer>
              </NavCol>
              <NavTitleCol>
                <h6>Sign In</h6>
              </NavTitleCol>
            </HyperLink>
          </NavRowUser>
        )}
        {userInfo && (
          <NavRowUser className="nav-user-row">
            <NavCol>
              <HyperLink
                href={
                  !isInPath(location.href, '/toolbox/bookmarks')
                    ? '/toolbox/bookmarks'
                    : history?.state?.prevUrl
                }
                state={{ prevPath: globalHistory.location.pathname }}
              >
                <UserIconContainer
                  className={isInPath(location.href, '/toolbox/bookmarks') ? 'selected' : ''}
                >
                  <BookmarksIcon width="28" height="23" />
                </UserIconContainer>
              </HyperLink>
            </NavCol>
            <NavCol>
              <UserIconContainer className={isPopoverOpen ? 'popoverOpen' : 'popoverClosed'}>
                <Popover
                  content={accountMenu}
                  placement={isMobile ? 'bottom-end' : 'right-end'}
                  className="toolbox-account-info-popover"
                  visible={isPopoverOpen}
                  onVisibleChange={(isVisible) => setIsPopoverOpen(isVisible)}
                  ButtonProps={{ style: { width: '100%' } }}
                  PaperProps={{ style: { marginLeft: '20px' } }}
                >
                  <div>
                    <UserAvatar userInfo={userInfo} width="30" height="30" />
                  </div>
                </Popover>
              </UserIconContainer>
            </NavCol>
          </NavRowUser>
        )}
      </SubNavContainer>
    </>
  );
};

export default SubNav;
